import { FFmpeg } from '@ffmpeg/ffmpeg';
import type Konva from 'konva';
import { toast } from 'react-toastify';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';

interface Point {
  x: number;
  y: number;
}

interface RGBColor {
  r: number;
  g: number;
  b: number;
}

export const limitDrag = (pos: Point, width: number, height: number): Point => ({
  x: Math.max(-width / 2, Math.min(pos.x, CANVAS_WIDTH - width / 2)),
  y: Math.max(-height / 2, Math.min(pos.y, CANVAS_HEIGHT - height / 2)),
});

export const parseColor = (hex: string): RGBColor => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

export const clampToHex = (val: number): string => {
  return Math.max(0, Math.min(255, Math.round(val)))
    .toString(16)
    .padStart(2, '0');
};

export const exportCanvasImage = (stage: Konva.Stage, format = 'png'): void => {
  if (!stage) return;

  const formatLower = format.toLowerCase();
  const canvasStore = useCanvasStore.getState();

  const originalState = {
    selectedId: canvasStore.selectedId,
    isEditingTextId: canvasStore.isEditingTextId,
  };

  try {
    useCanvasStore.setState({
      selectedId: null,
      isEditingTextId: null,
    });

    setTimeout(() => {
      stage.batchDraw();

      const dataURL = stage.toDataURL({
        mimeType: `image/${formatLower}`,
        quality: formatLower === 'jpeg' ? 0.95 : undefined,
        pixelRatio: 2,
      });

      const link = document.createElement('a');
      const extension = formatLower === 'jpeg' ? 'jpg' : formatLower;
      link.download = `canvas-${new Date().toISOString().slice(0, 10)}.${extension}`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      useCanvasStore.setState({
        selectedId: originalState.selectedId,
        isEditingTextId: originalState.isEditingTextId,
      });
    }, 50);
  } catch (error) {
    toast.error('Failed to export image');

    useCanvasStore.setState({
      selectedId: originalState.selectedId,
      isEditingTextId: originalState.isEditingTextId,
    });
  }
};

interface GIFExportOptions {
  framesPerSecond: number;
  durationInSeconds: number;
  quality: number;
}

const DEFAULT_GIF_OPTIONS: GIFExportOptions = {
  framesPerSecond: 60,
  durationInSeconds: 3,
  quality: 1.0,
};

export const exportCanvasGif = async (stage: Konva.Stage, shouldDownload = true, callback?: (blob: Blob) => void) => {
  if (!stage) return;

  const loadingElement = document.createElement('div');
  loadingElement.className = 'canvas-loading-overlay';
  loadingElement.innerHTML = `
    <div class="canvas-loading-spinner"></div>
    <div class="canvas-loading-text">Preparing animation...</div>
  `;
  document.body.appendChild(loadingElement);

  const canvasStore = useCanvasStore.getState();
  const originalState = {
    previewMode: canvasStore.previewMode,
    selectedId: canvasStore.selectedId,
    isEditingTextId: canvasStore.isEditingTextId,
  };

  try {
    useCanvasStore.setState({
      selectedId: null,
      isEditingTextId: null,
      previewMode: false,
    });

    loadingElement.innerHTML = `
      <div class="canvas-loading-spinner"></div>
      <div class="canvas-loading-text">Initializing...</div>
    `;
    const ffmpeg = new FFmpeg();
    await ffmpeg.load();

    const exportOptions = {
      framesPerSecond: 24,
      durationInSeconds: 3,
      quality: 2.0,
      frameDelay: Math.floor(1000 / 24),
    };
    const totalFrames = exportOptions.framesPerSecond * exportOptions.durationInSeconds;
    const frames = [];

    loadingElement.innerHTML = `
      <div class="canvas-loading-spinner"></div>
      <div class="canvas-loading-text">Starting animation capture...</div>
    `;

    useCanvasStore.setState({ previewMode: true });

    await new Promise<void>((resolve) => setTimeout(resolve, 50));

    for (let i = 0; i < totalFrames; i++) {
      const frameStartTime = Date.now();

      stage.batchDraw();

      const dataURL = stage.toDataURL({
        pixelRatio: exportOptions.quality,
        mimeType: 'image/png',
      });
      frames.push(dataURL);

      // Show progress
      const progressPercent = Math.round((i / totalFrames) * 100);
      loadingElement.innerHTML = `
        <div class="canvas-loading-spinner"></div>
        <div class="canvas-loading-text">Capturing animation: ${progressPercent}%</div>
      `;

      const elapsedTime = Date.now() - frameStartTime;
      const waitTime = Math.max(0, exportOptions.frameDelay - elapsedTime);
      await new Promise<void>((resolve) => setTimeout(resolve, waitTime));
    }

    useCanvasStore.setState({ previewMode: false });

    loadingElement.innerHTML = `
      <div class="canvas-loading-spinner"></div>
      <div class="canvas-loading-text">Processing frames...</div>
    `;

    for (let i = 0; i < frames.length; i++) {
      try {
        const filename = `frame_${i.toString().padStart(4, '0')}.png`;
        const res = await fetch(frames[i]);
        const blob = await res.blob();
        const buffer = await blob.arrayBuffer();
        await ffmpeg.writeFile(filename, new Uint8Array(buffer));

        if (i % 5 === 0) {
          const progressPercent = Math.round((i / frames.length) * 100);
          loadingElement.innerHTML = `
            <div class="canvas-loading-spinner"></div>
            <div class="canvas-loading-text">Processing frames: ${progressPercent}%</div>
          `;
        }
      } catch (error) {
        throw new Error(`Failed to write frame ${i}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    loadingElement.innerHTML = `
      <div class="canvas-loading-spinner"></div>
      <div class="canvas-loading-text">Creating GIF...</div>
    `;

    await ffmpeg.exec([
      '-framerate',
      exportOptions.framesPerSecond.toString(),
      '-i',
      'frame_%04d.png',
      '-vf',
      'split[s0][s1];[s0]palettegen=max_colors=256:reserve_transparent=0[p];[s1][p]paletteuse=dither=sierra2_4a',
      '-loop',
      '0',
      '-y',
      'output.gif',
    ]);

    let fileData: ArrayBufferLike;
    const rawFileData = await ffmpeg.readFile('output.gif');
    if (rawFileData instanceof Uint8Array) {
      fileData = rawFileData.buffer;
    } else {
      fileData = await new Blob([rawFileData]).arrayBuffer();
    }

    const gifBlob = new Blob([new Uint8Array(fileData)], { type: 'image/gif' });

    if (gifBlob.size === 0) {
      throw new Error('Generated GIF has zero size');
    }

    if (shouldDownload) {
      const url = URL.createObjectURL(gifBlob);
      const link = document.createElement('a');
      link.download = `canvas-animation-${new Date().toISOString().slice(0, 10)}.gif`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    if (callback && typeof callback === 'function') {
      callback(gifBlob);
    }

    return gifBlob;
  } catch (error) {
    toast.error(`Failed to generate GIF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return null;
  } finally {
    document.body.removeChild(loadingElement);
    useCanvasStore.setState({
      previewMode: originalState.previewMode,
      selectedId: originalState.selectedId,
      isEditingTextId: originalState.isEditingTextId,
    });
  }
};
